import React from 'react';

import { graphql, Link, PageProps } from 'gatsby';

import { Button, Grid2 as Grid, styled } from '@mui/material';

import {
  isCleanroom,
  ServiceSegmentCategory,
  ServiceSegmentType,
  ServiceTeaserData,
  ServiceTeaserType,
} from '@mewa/types';
import {
  BreadCrumbData,
  MewaCircleData,
  ResponsiveGrid,
  SeoFooter,
  ServiceTeaser,
} from '@mewa/ui';

import t from '../../../libs/translate';
import { useConversionButtonLabel } from '../../a-b-testing/cta-label-experiment';
import { ContactTeaserWrapper } from '../../components/contact-teaser/contact-teaser-wrapper';
import { NavigationData } from '../../components/header/types';
import { GlobalPageContext } from '../../types';
import CollectionPage, {
  Collection,
} from '../components/collection-page/collection-page';
import { prepareBrochureSelection } from '../conversion/brochures-download/helper';
import { storeServiceSegment } from '../conversion/persistence';
import { ContentSection } from '../dynamic-content-page/content-section';
import { PossibleContentSections } from '../dynamic-content-page/fragment-and-types';
import { possibleComponents } from './content-section';

export { Head } from '../../layouts/head';

type Props = NavigationData & {
  // This is not really a collection, but we interpret the service segment data
  // as a collection for all none-clothing service segments.
  pimServiceSegment: Omit<Collection, 'products'> & {
    key: ServiceSegmentType;
    category: ServiceSegmentCategory;
    productGroups: {
      products: Collection['products'];
    }[];
    seoFooter: {
      childMarkdownRemark: { html: string };
    };
  };
  serviceTestTeaser?: ServiceTeaserData<ServiceTeaserType.cleaningClothsServiceTest>;
  strapiServiceSegment: {
    content: PossibleContentSections[];
    mewaCircle: MewaCircleData | null;
  } | null;
  translations: {
    general_more_information: string;
    general_download_brochure: string;
    show_product_details_label: string;
    seo_footer_show_more_label: string;
    seo_footer_show_less_label: string;
    navigation_product_menu_label: string;
  };
};

type PageContext = GlobalPageContext;

export const query = graphql`
  query (
    $marketRegex: String!
    $id: String!
    $lang: String!
    $locale: String!
    $uuid: String
  ) {
    ...NavigationFragment

    pimServiceSegment(id: { eq: $id }) {
      name
      slug
      claim
      property
      infoText1
      infoText2
      galleryTitle
      galleryIntroText
      key
      category
      productGroups {
        products {
          ...ProductData
        }
      }
      seoFooter {
        childMarkdownRemark {
          html
        }
      }
      assets {
        keyVisualDesktop
        keyVisualMobile
      }
    }

    serviceTestTeaser: strapiServiceTeaser(
      ServiceType: { eq: "putztuch-service-test" }
      locale: { eq: $locale }
    ) {
      description: Description
      title: Title
      serviceType: ServiceType
    }

    strapiServiceSegment(locale: { eq: $locale }, pimUuid: { eq: $uuid }) {
      ...ContentSectionFragment

      mewaCircle {
        ...MewaCircleFragment
      }
    }

    translations: translation(locale: { eq: $locale }) {
      general_more_information
      general_download_brochure
      show_product_details_label
      seo_footer_show_more_label
      seo_footer_show_less_label
      navigation_product_menu_label
    }
  }
`;

const NonClothingServiceSegment = ({
  data: {
    pimServiceSegment: collection,
    serviceTestTeaser,
    translations,
    navigationLinks,
    contactTeaserLabels,
    headerAndFooterTranslations,
    strapiServiceSegment,
  },
  pageContext: { langPrefix, locale, lang },
}: PageProps<Props, PageContext>) => {
  const { ctaLabel } = useConversionButtonLabel({
    translations: headerAndFooterTranslations,
  });

  const collectionData = {
    ...collection,
    description: collection.infoText1, // for non clothing service segments displayed
    // with the collections template, we use the infoText1 as the description
    infoText1: null,
    products: collection.productGroups[0]?.products ?? [],
  };

  const categoryBreadCrumb = isCleanroom(collection)
    ? {
        name: navigationLinks.navigation.cleanroom.name,
        slug: `${langPrefix}/${navigationLinks.navigation.cleanroom.slug}/`,
      }
    : {
        name: navigationLinks.navigation.nonClothing.name,
        slug: `${langPrefix}/${navigationLinks.navigation.nonClothing.slug}/`,
      };

  const breadCrumbData: BreadCrumbData[] = [
    {
      name: t(translations, 'navigation_product_menu_label'),
      slug: `${langPrefix}/${navigationLinks.navigation.productMenu.slug}/`,
    },
    categoryBreadCrumb,
    {
      name: collectionData.name,
      slug: '',
    },
  ];

  const mobileBreadCrumbData: BreadCrumbData = {
    name: navigationLinks.navigation.nonClothing.name,
    slug: `${langPrefix}/${navigationLinks.navigation.nonClothing.slug}/`,
  };

  return (
    <>
      <CollectionPage
        data-pagefind-meta={`service_segment_type:${collection.key}`}
        isClothingCategory={false}
        langPrefix={langPrefix}
        collection={collectionData}
        breadCrumbData={breadCrumbData}
        mobileBreadCrumbData={mobileBreadCrumbData}
        locale={locale}
        brochuresDownload={{
          text: translations.general_download_brochure,
          url: navigationLinks.conversion.brochuresDownload.slug,
          preSelection: [prepareBrochureSelection(collection.key)],
        }}
        detailsPageLabel={t(translations, 'show_product_details_label')}
        conversion={{
          label: ctaLabel,
          link: `${langPrefix}/${navigationLinks.conversion.contact.slug}`,
          onClick: () => {
            storeServiceSegment(collection.key);
          },
        }}
        serviceTestTeaser={
          collection.category !== 'MRS' && serviceTestTeaser ? (
            <ServiceTeaser
              data-pagefind-ignore
              description={serviceTestTeaser.description}
              title={serviceTestTeaser.title}
              type={ServiceTeaserType.cleaningClothsServiceTest}
              link={
                <Button
                  variant="text"
                  color="secondary"
                  component={Link}
                  to={`${langPrefix}/${navigationLinks.conversion.cleaningClothsServiceTestPage.slug}`}
                >
                  {t(translations, 'general_more_information')}
                </Button>
              }
            />
          ) : undefined
        }
      />

      {strapiServiceSegment && strapiServiceSegment.content !== null ? (
        <ContentResponsiveGrid>
          <Grid size={12}>
            <ContentSection
              possibleComponents={possibleComponents}
              conversion={{ label: '', link: '' }}
              lang={lang}
              locale={locale}
              langPrefix={langPrefix}
              sections={strapiServiceSegment.content}
              navigationLinks={navigationLinks}
              translations={{ author_box_by: '' }}
            />
          </Grid>
        </ContentResponsiveGrid>
      ) : null}

      <ContactTeaserWrapper
        contactTeaserLabels={contactTeaserLabels}
        langPrefix={langPrefix}
        navigationLinks={navigationLinks}
        serviceSegment={collection.key}
      />
      {collection.seoFooter.childMarkdownRemark.html ? (
        <SeoFooter
          html={collection.seoFooter.childMarkdownRemark.html}
          showMoreLabel={t(translations, 'seo_footer_show_more_label')}
          showLessLabel={t(translations, 'seo_footer_show_less_label')}
        />
      ) : null}
    </>
  );
};

NonClothingServiceSegment.noContactTeaser = true;

export default NonClothingServiceSegment;

const ContentResponsiveGrid = styled(ResponsiveGrid)({
  marginTop: 'var(--spacer-xxxl)',
});
